/** Normalize the API response to [{id, label, city?, state?, street?}] */
export function normalizeAutocomplete(data) {
  // Add defensive checks for the API response structure
  if (!data || !data.Result || !data.Result.autoCompleteResponse) {
    console.warn("Invalid API response structure:", data);
    return [];
  }

  const buckets = Object.values(data.Result.autoCompleteResponse);

  const suggestions = buckets.flatMap(b =>
    Array.isArray(b?.suggestions) ? b.suggestions : [],
  );

  return suggestions
    .map((r, i) => {
      return {
        id:
          (r.street || "") + "|" + (r.city || "") + "|" + (r.state || "") ||
          String(i),
        label: r.text || "",
        street: r.street || "",
        city: r.city || "",
        state: r.state || "",
      };
    })
    .filter(x => x.label);
}

/**
 * Create a debounced function that delays execution until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Global debounced request function - singleton pattern
let globalAbortController = null;
let globalDebounceTimeout = null;
let pendingPromises = [];
let currentPrefix = "";

const debouncedApiRequest = async prefix => {
  return new Promise(resolve => {
    // Store this promise
    pendingPromises.push(resolve);
    currentPrefix = prefix;

    // Clear existing timeout
    if (globalDebounceTimeout) {
      clearTimeout(globalDebounceTimeout);
    }

    // Set new debounced timeout
    globalDebounceTimeout = setTimeout(async () => {
      const allResolvers = [...pendingPromises];
      const prefixToFetch = currentPrefix;
      pendingPromises = []; // Clear the array

      try {
        // Abort previous request if it's still pending
        if (globalAbortController) globalAbortController.abort();
        // Create new controller for current request
        globalAbortController = new AbortController();

        const url = new URL("api/ESB/Autocomplete", window.location.origin);
        url.searchParams.set("prefix", prefixToFetch);

        const res = await fetch(url.toString(), {
          signal: globalAbortController.signal,
          headers: { Accept: "application/json" },
        });

        const data = res.ok ? await res.json() : null;
        const result = normalizeAutocomplete(data);

        // Resolve all pending promises with the same result
        allResolvers.forEach(resolver => resolver(result));
      } catch (error) {
        console.error("Error fetching autocomplete suggestions:", error);
        // Resolve all pending promises with empty array on error
        allResolvers.forEach(resolver => resolver([]));
      }
    }, 500);
  });
};

/**
 * Create a "server" source for the autocomplete.
 * Usage: const serverSource = makeAutocompleteServerSource();
 *        const items = await serverSource(query);
 */
export function makeAutocompleteServerSource() {
  return function serverSource(prefix) {
    return debouncedApiRequest(prefix);
  };
}
